/**
 * Module-level session cache for the Home dashboard (Welcome page).
 *
 * Why this exists:
 *   The dashboard's three loaders — /api/customers (with health), the
 *   /api/customers/metrics scan, and N parallel /resolved-facilities calls
 *   — are noticeably slow against the cloud SQLite-on-EFS backend, and
 *   React Router unmounts the page when the user navigates away. Without
 *   this cache, every "Home → Data Trends → Home" round-trip pays the full
 *   reload cost. With it, return-trips are instant and a quiet background
 *   refresh keeps the data current.
 *
 * Behavior (stale-while-revalidate):
 *   - Hydrated on first successful fetch.
 *   - Read on every mount; entries fresher than SESSION_TTL_MS are returned
 *     as-is and no fetch is made.
 *   - Stale entries are STILL returned synchronously (so the page renders
 *     instantly) and the page kicks off a background refresh that updates
 *     the cards in place when it returns.
 *   - Explicitly cleared on sign-out (AuthContext.logout) and on
 *     'cache-refreshed' pub-sub events from the Data Sources page so the
 *     dashboard reflects a fresh refresh immediately, regardless of TTL.
 *   - Naturally cleared by a full browser reload (it's just a JS module).
 *
 * Cache keys:
 *   - customers: single slot — customer list rarely changes.
 *   - stats: keyed by the sorted set of customer IDs so a new assignment
 *     forces a refetch but a no-op customer reload reuses the cache.
 *   - metrics: keyed by the comparison-window date strings so toggling the
 *     "Compare:" dropdown back to a previous value is also instant.
 */

export const SESSION_TTL_MS = 10 * 60 * 1000;

const cache = {
  customers: null,
  stats: null,
  metrics: new Map(),
};

export function metricsCacheKey(range) {
  if (!range) return '';
  return `${range.currentStart}|${range.currentEnd}|${range.previousStart}|${range.previousEnd}`;
}

export function statsIdsKey(customers) {
  return (customers || [])
    .map(c => Number(c.id))
    .sort((a, b) => a - b)
    .join(',');
}

export function isFresh(entry, ttl = SESSION_TTL_MS) {
  return entry?.data != null && Date.now() - entry.fetchedAt < ttl;
}

export function getCachedCustomers() {
  return cache.customers;
}

export function setCachedCustomers(data) {
  cache.customers = { data, fetchedAt: Date.now() };
}

export function getCachedStats(idsKey) {
  if (!cache.stats || cache.stats.idsKey !== idsKey) return null;
  return cache.stats;
}

export function setCachedStats(data, idsKey) {
  cache.stats = { data, fetchedAt: Date.now(), idsKey };
}

export function getCachedMetrics(key) {
  return cache.metrics.get(key) || null;
}

export function setCachedMetrics(key, data) {
  cache.metrics.set(key, { data, fetchedAt: Date.now() });
}

export function invalidateWelcomeSessionCache() {
  cache.customers = null;
  cache.stats = null;
  cache.metrics.clear();
}
